import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { frontlineUsersAdminApi } from '@/api/client';
import type {
  FrontlineUsersAdminPageDto, FrontlineUsersFilters, FrontlineVerificationEventDto,
  FrontlineVerificationSendResultDto, FrontlineUserStateDto, FrontlinePasswordResetSendResultDto,
  TemporaryPasswordDto,
} from '@/api/types';

/** Raíz de todas las claves de «Gestión de usuarios»: invalidarla refresca listado, contadores e historiales. */
export const FRONTLINE_USERS_ADMIN_KEY = ['frontline-users-admin'] as const;
const KEY = FRONTLINE_USERS_ADMIN_KEY;

/**
 * Página del listado con sus contadores. Se conserva la página anterior mientras llega la nueva
 * (`keepPreviousData`): al teclear en la búsqueda la tabla no parpadea en blanco entre respuestas.
 */
export function useFrontlineUsers(filters: FrontlineUsersFilters, page: number, pageSize: number) {
  return useQuery<FrontlineUsersAdminPageDto>({
    queryKey: [...KEY, 'list', filters, page, pageSize],
    queryFn: () => frontlineUsersAdminApi.list(filters, page, pageSize),
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** Historial de una cuenta. Solo se pide cuando el modal está abierto. */
export function useFrontlineVerificationHistory(id: string | null) {
  return useQuery<FrontlineVerificationEventDto[]>({
    queryKey: [...KEY, 'history', id],
    queryFn: () => frontlineUsersAdminApi.history(id as string),
    enabled: id !== null,
    retry: false,
  });
}

function useInvalidating<TVars, TData>(fn: (vars: TVars) => Promise<TData>) {
  const queryClient = useQueryClient();
  return useMutation<TData, Error, TVars>({
    mutationFn: fn,
    // Cualquier acción cambia el estado de una fila, los contadores y el historial: se refresca el conjunto completo.
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: KEY }); },
  });
}

export function useSendFrontlineVerification() {
  return useInvalidating<string, FrontlineVerificationSendResultDto>(id => frontlineUsersAdminApi.sendVerification(id));
}

export function useUpdateFrontlineEmail() {
  return useInvalidating<{ id: string; correoPersonal: string | null }, FrontlineUserStateDto>(
    ({ id, correoPersonal }) => frontlineUsersAdminApi.updateEmail(id, correoPersonal),
  );
}

export function useMarkFrontlineEmailVerified() {
  return useInvalidating<string, FrontlineUserStateDto>(id => frontlineUsersAdminApi.markVerified(id));
}

export function useSendFrontlinePasswordReset() {
  return useInvalidating<string, FrontlinePasswordResetSendResultDto>(id => frontlineUsersAdminApi.sendPasswordReset(id));
}

/**
 * Genera la contraseña temporal. 🔴 El resultado trae la contraseña EN CLARO: `gcTime: 0` hace que React Query no la
 * conserve en su caché de mutaciones (por omisión guardaría el resultado unos minutos). Quien la pide la guarda en
 * estado local del modal y la descarta al cerrarlo.
 */
export function useGenerateTemporaryPassword() {
  const queryClient = useQueryClient();
  return useMutation<TemporaryPasswordDto, Error, { id: string; forceChange: boolean }>({
    mutationFn: ({ id, forceChange }) => frontlineUsersAdminApi.generateTemporaryPassword(id, forceChange),
    gcTime: 0,
    // Cambia la contraseña de la cuenta y el aviso de «temporal sin usar»: se refresca el listado.
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: KEY }); },
  });
}
